import React, { memo, useEffect, useState } from "react";

import { useUppy } from "@uppy/react";
import Url from "@uppy/url";
import Uppy, { UploadResult, UppyFile } from "@uppy/core";
import AwsS3Multipart from "@uppy/aws-s3-multipart";
import XHRUpload from "@uppy/xhr-upload";
import ImageCompressor from "@/Hooks/useImageCompressor";
import {
	IFileUploadProps,
	UploadStage,
	UppyAllowedFileTypes,
	NotAllowedExtensions,
	IOnBeforeUploadFunction,
	IOnBeforeAddedFunction,
	UppyFileMetaData
} from "./interface";

import ZButton from "@atoms/ZButton";
import ZTypography from "@atoms/ZTypography";
import { EFontWeights } from "@atoms/ZTypography/interface";
import { EColors } from "@atoms/ZColors/interface";

import { validateYoutubeLink } from "@/Utils";

import styles from "./index.module.less";
import "./index.less";

import ZRenderStageContent from "./ZRenderStageContent";
import {
	FinishedStageState,
	ProgressStageState,
	UploadLinkStageState
} from "./ZRenderStageContent/interface";
import flatMap from "lodash/flatMap";
import ZModal from "@atoms/ZModal";

import ZMessage from "../ZMessage";
import { cache } from "@/Contracts";
import { validUrlRegex } from "@/Utils";
import { sanitizeFilename } from "@/Utils";

const { ZText } = ZTypography;
const FILES_HOST = "https://files.zomentumstage.com";
const API_HOST = "https://apiv2.channelforcestage.com";

const ZFileUpload = memo(
	({
		showFileMetaInfoText = true,
		compressImages = true,
		allowYoutubeLink = true,
		alert,
		...props
	}: IFileUploadProps) => {
		const [uploadedFile, setUploadedFile] = useState<Uppy.UppyFile | null>(
			null
		);
		const [loadingPercentage, setLoadingPercentage] = useState<number>(0);
		const [invalidExtensionError, setInvalidExtensionError] =
			useState(false);
		const [isYoutubeVideoLink, setIsYoutubeVideoLink] = useState(false);
		const [uploadStage, setUploadStage] = useState<UploadStage>(
			UploadStage.FILE_SELECT
		);
		const [link, setLink] = useState("");

		const resetState = () => {
			uppy.reset();
			setLoadingPercentage(0);
			setUploadStage(UploadStage.FILE_SELECT);
			setLink("");
		};

		useEffect(() => {
			uploadedFile &&
				ZMessage.success(
					props.uploadSuccessMessage ?? "File Uploaded successfully"
				);
		}, [uploadedFile]);

		useEffect(() => {
			if (props.isEmailAttachment) {
				const instance = uppy.getPlugin("AwsS3Multipart");
				uppy.removePlugin(instance);
				if (props.skipUploadingToNylas) {
					const emailAttachmentInstance =
						uppy.getPlugin("attachmentUpload");
					uppy.removePlugin(emailAttachmentInstance);
				}
			}

			return () => {
				resetState();
			};
		}, []);

		const allowMultipleUploads = props.allowMultipleUploads ?? false,
			maxFileSize = 500 * 1024 * 1024; // 500 MB

		const isImage =
			!!props.allowedFileTypes?.length &&
			props.allowedFileTypes.includes(UppyAllowedFileTypes.IMAGE);

		const isVideo =
			!!props.allowedFileTypes?.length &&
			props.allowedFileTypes.includes(UppyAllowedFileTypes.VIDEO);

		const isPDF =
			!!props.allowedFileTypes?.length &&
			props.allowedFileTypes.includes(UppyAllowedFileTypes.PDF);

		const isTXT =
			!!props.allowedFileTypes?.length &&
			props.allowedFileTypes.includes(UppyAllowedFileTypes.TXT);

		const isCSV =
			!!props.allowedFileTypes?.length &&
			props.allowedFileTypes.includes(UppyAllowedFileTypes.CSV);

		const isPPTX =
			!!props.allowedFileTypes?.length &&
			props.allowedFileTypes.includes(UppyAllowedFileTypes.PPTX);

		const isDOCX =
			!!props.allowedFileTypes?.length &&
			props.allowedFileTypes.includes(UppyAllowedFileTypes.DOCX);

		const isXLSX =
			!!props.allowedFileTypes?.length &&
			props.allowedFileTypes.includes(UppyAllowedFileTypes.XLSX);

		const isAll = props.allowedFileTypes?.includes(
			UppyAllowedFileTypes.ALL
		);
		const uppyRestrictions = {
			maxFileSize: props.maxFileSize
				? props.maxFileSize < maxFileSize
					? props.maxFileSize
					: maxFileSize
				: maxFileSize,
			maxNumberOfFiles: props.maxNumberOfFiles
				? props.maxNumberOfFiles
				: 1,
			minNumberOfFiles: props.minNumberOfFiles
				? props.minNumberOfFiles
				: 1,
			allowedFileTypes: !!props.allowedFileTypes?.length
				? flatMap(props.allowedFileTypes, fileType =>
						fileType.split(",")
				  )
				: [UppyAllowedFileTypes.ALL]
		};
		const isValidBase64 = (link: string): boolean => {
			let isBase64 = false;
			const knownTypes = [
				"data:image/jpg;base64",
				"data:image/png;base64",
				"data:image/jpeg;base64"
			];

			knownTypes.forEach(type => {
				if (link.indexOf(type) >= 0) {
					isBase64 = true;
				}
			});
			return isBase64;
		};
		const onBeforeFileAddedFunction: IOnBeforeAddedFunction = (
			currentFile,
			_files
		) => {
			const modifiedFile = {
				...currentFile,
				name: sanitizeFilename(currentFile?.name) ?? "",
				meta: {
					...currentFile.meta,
					name: sanitizeFilename(currentFile?.meta?.name) ?? ""
				}
			};
			const shouldNotUpload = NotAllowedExtensions.test(currentFile.name);
			if (shouldNotUpload) {
				setInvalidExtensionError(true);
				return false;
			}
			if (props.path) {
				modifiedFile.name = `${props.path}/${
					sanitizeFilename(currentFile?.name) ?? ""
				}`;
				return modifiedFile;
			}
			return modifiedFile;
		};

		const onBeforeUploadFunction: IOnBeforeUploadFunction = files => {
			Object.keys(files).forEach(fileId => {
				if (fileId && files[fileId]?.data) {
					if (files[fileId].source !== "MyUrl") {
						const file = files[fileId];
						const data = file.data;
						const url = URL.createObjectURL(data);
						if (isImage) {
							const image = new Image();
							image.src = url;
							image.onload = () => {
								uppy.setFileMeta(fileId, {
									width: image.width,
									height: image.height
								});
								URL.revokeObjectURL(url);
							};
						} else if (isVideo) {
							uppy.setFileMeta(fileId, { thumbnail: "" });
						}
					} else {
						if (isImage) {
							const width = 600,
								height = 300;
							uppy.setFileMeta(fileId, { width, height });
						} else if (isVideo) {
							uppy.setFileMeta(fileId, { thumbnail: "" });
						}
					}
				}
			});
			return files;
		};

		const onFileAddedEvent = () => {
			props.onUploadStart?.();
			setUploadStage(UploadStage.UPLOADING_IN_PROGRESS);
		};

		const onProgressEvent = (progress: number) => {
			setLoadingPercentage(progress);
		};

		const onCompleteEvent = (
			result: Uppy.UploadResult<UppyFileMetaData>
		) => {
			const fileState =
				!!result.successful.length &&
				uppy.getFile(result.successful[0].id);
			if (fileState) {
				props.onUploadSuccess?.(result);
			}

			if (props.isEmailAttachment && result.failed.length > 0) {
				setUploadedFile(result.failed[0]);
				props.onCancel?.();
			} else setUploadedFile(result.successful[0]);

			if (!allowMultipleUploads) {
				props.onCancel?.();
			}

			resetState();
		};

		const onUploadSuccess = (file: UppyFile, response: any) => {
			const fileState = file && uppy.getFile(file.id);
			if (fileState) {
				props.onEachUploadSuccess?.(file, response);
			}
		};

		const onErrorEvent = (error: any) => {
			console.error(error);
			ZMessage.error(
				props.errorMessage ??
					"There was an error in uploading your file, please try again"
			);

			resetState();
		};

		const onRestrictionFailedEvent = (file: UppyFile, error: any) => {
			if (
				(file.type?.includes("text/plain") ||
					file.type?.includes("text/html")) &&
				file.remote &&
				file.remote.body
			) {
				const url = Object.values(file.remote.body)[1] as string;
				if (!allowYoutubeLink) {
					ZMessage.error("Youtube links are not supported");
					return;
				} else if (validateYoutubeLink(url)) {
					const result: UploadResult<UppyFileMetaData> = {
						successful: [{ uploadURL: url, ...file }],
						failed: []
					};
					props.onUploadSuccess?.(result);
					if (!allowMultipleUploads) props.onCancel?.();
					resetState();
					return;
				}
				ZMessage.error("Invalid Link");
			} else if (invalidExtensionError) {
				ZMessage.error(`${file.name} has an invalid extension`);
			} else {
				if (uploadStage !== UploadStage.UPLOAD_LINK) {
					ZMessage.error(error.toString());
				}
			}
		};

		const uppy = useUppy(() => {
			const uppyOptions = {
				id: "MyUppy",
				autoProceed: true,
				restrictions: uppyRestrictions,
				allowMultipleUploads,
				onBeforeFileAdded: onBeforeFileAddedFunction,
				onBeforeUpload: onBeforeUploadFunction
			};

			const uppy = Uppy<Uppy.TypeChecking>(uppyOptions)
				.use(AwsS3Multipart, {
					id: "AwsS3Multipart",
					companionUrl: process.env.REACT_APP_FILES_HOST ?? FILES_HOST
				})
				.use(Url, {
					id: "MyUrl",
					companionUrl: process.env.REACT_APP_FILES_HOST ?? FILES_HOST
				});

			if (isImage && compressImages) uppy.use(ImageCompressor);

			// ZomentumStorageKeys.Token = "token" and we are using similar key everywhere. hence hardcoding it here for now.
			if (props.isEmailAttachment) {
				const xhrOptions = {
					formData: true,
					fieldName: "file",
					headers: {
						Authorization: `Bearer ${
							cache.get("token", true).token
						}`
					}
				};
				const endpoint = process.env.REACT_APP_API_HOST ?? API_HOST;
				uppy.use(XHRUpload, {
					id: "fileUpload",
					endpoint: `${endpoint}/v2/files/upload`,
					...xhrOptions
				}).use(XHRUpload, {
					id: "attachmentUpload",
					endpoint: `${endpoint}/v2/emails/attachments/upload`,
					...xhrOptions
				});
			}

			return uppy
				.on("file-added", onFileAddedEvent)
				.on("progress", onProgressEvent)
				.on("complete", onCompleteEvent)
				.on("upload-success", onUploadSuccess)
				.on("error", onErrorEvent)
				.on("restriction-failed", onRestrictionFailedEvent);
		});

		const handleUploadViaURL = () =>
			setUploadStage(UploadStage.UPLOAD_LINK);
		const uploadViaLink = async () => {
			try {
				// In the latest stable version of @uppy/url, the PluginOption type does not contain addFile method in it's definition.
				// Ref: https://uppy.io/docs/url/#addFile
				// Source Code: https://github.com/transloadit/uppy/raw/%40uppy/url%401.5.23/packages/%40uppy/url/types/index.d.ts
				const urlPlugin: any = uppy.getPlugin("MyUrl");
				if (isValidBase64(link)) {
					ZMessage.error(`Base64 file is not supported`);
					return;
				}
				if (!link.match(validUrlRegex)) {
					ZMessage.error("Please enter a valid file Url!");
					return;
				}
				await urlPlugin.addFile(link);
			} catch (err) {
				console.error(err);
			}
		};

		const handleInputLinkChange = (
			e: React.ChangeEvent<HTMLInputElement>
		) => {
			const isYoutubeVideo = validateYoutubeLink(e.target.value);
			if (isYoutubeVideo) setIsYoutubeVideoLink(true);
			else setIsYoutubeVideoLink(false);

			setLink(e.target.value);
		};

		const onClose = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
			resetState();
			if (props.onCancel) props.onCancel();
			if (props.useModal?.onCancel) props.useModal.onCancel(e);
		};

		const linkState: UploadLinkStageState = {
			isImage: props.useModal?.title !== "Upload template",
			isVideo,
			isPDF,
			isTXT,
			isCSV,
			isPPTX,
			isDOCX,
			isXLSX,
			isAll,
			showFileMetaInfoText: showFileMetaInfoText,
			handleInputLinkChange,
			linkValue: link,
			isYoutubeVideoLink
		};

		const progressState: ProgressStageState = {
			loadingPercentage,
			uploadingText: props.uploadingText
		};

		const finishedState: FinishedStageState = {
			uploadSuccessMessage: props.uploadSuccessMessage,
			file: uploadedFile
		};

		const componentToRender = (
			<>
				<div
					className={`
		${styles.zUploadModalWrapper}
		border bg-gray-3 border-gray-6 border-dashed 
		relative
		zUploadModalWrapper
	`}
					onClick={e => e.stopPropagation()}
				>
					<ZRenderStageContent
						stage={uploadStage}
						uppy={uppy}
						linkState={linkState}
						progressState={progressState}
						finishedState={finishedState}
						maxFileSize={props?.maxFileSize}
					/>
				</div>
				{uploadStage === UploadStage.FILE_SELECT ? (
					<div
						className={`w-full flex ${
							props.useURL ? "justify-between" : "justify-end"
						} pt-3`}
					>
						{props.useURL && (
							<ZButton
								type="link"
								className="pl-0"
								onClick={handleUploadViaURL}
							>
								<ZText
									colorVariant={6}
									color={EColors.BLUE}
									weight={EFontWeights.MEDIUM}
								>
									Add via link
								</ZText>
							</ZButton>
						)}
						<ZButton
							onClick={props.onCancel ?? props.useModal?.onCancel}
							className={!props.useModal ? "hidden" : ""}
						>
							Cancel
						</ZButton>
					</div>
				) : (
					uploadStage === UploadStage.UPLOAD_LINK && (
						<div className="flex justify-between w-full pt-3">
							<ZButton
								onClick={() =>
									setUploadStage(UploadStage.FILE_SELECT)
								}
							>
								Back
							</ZButton>
							<div>
								<ZButton onClick={onClose} className="mr-2">
									Cancel
								</ZButton>
								<ZButton
									type="primary"
									onClick={uploadViaLink}
									disabled={!link.length}
								>
									Confirm
								</ZButton>
							</div>
						</div>
					)
				)}
			</>
		);

		const hasAlert = !!alert;

		return !!props.useModal ? (
			<ZModal
				size="sm"
				footer={false}
				maskClosable={false}
				onCancel={onClose}
				className={`zUploader ${hasAlert ? "remove-body-pad" : ""}`}
				{...props.useModal}
			>
				{alert}
				<div className={hasAlert ? "p-6" : ""}>{componentToRender}</div>
			</ZModal>
		) : (
			componentToRender
		);
	}
);

export default ZFileUpload;
